import { motion, AnimatePresence } from 'framer-motion';
import { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import axios from 'axios';
import { BASE_API_URL } from '../helpers/Config';
import { useNavigate } from 'react-router-dom';
import { TriggerBalanceUpdate } from '../helpers/TriggerBalanceUpdate';
import moment from 'moment';

const CouponScreen = ({ theme = 'dark' }) => {
    const [couponCode, setCouponCode] = useState('');
    const [claimedCoupons, setClaimedCoupons] = useState([]);
    const [isLoading, setIsLoading] = useState(false);

    const navigate = useNavigate();

    // derive dark mode from prop
    const isDarkMode = theme === 'dark';

    const FetchClaimCoupons = async () => {
        try {
            const response = await axios.post(`${BASE_API_URL}/payment/redeem-history`, {
                token: localStorage.getItem('a5srf4wer7w1cd'),
            });

            const data = response?.data;

            if (data?.error) {
                if (data?.error === 'session expired') {
                    localStorage.clear('a5srf4wer7w1cd');
                    navigate('/login');
                } else {
                    toast.error(data?.error);
                }
            }

            if (data?.data) {
                setClaimedCoupons(data?.data);
            }
        } catch (error) {
            toast.error('Technical error');
            console.log(error);
        }
    };

    const handleClaimCoupon = async (e) => {
        e.preventDefault();

        if (!couponCode.trim()) {
            toast.error('Please enter a coupon code');
            return;
        }

        setIsLoading(true);

        try {
            const response = await axios.post(`${BASE_API_URL}/payment/coupon-claim`, {
                token: localStorage.getItem('a5srf4wer7w1cd'),
                coupon_code: couponCode
            });

            const data = response?.data;

            if (data?.error) {
                if (data?.error === 'session expired') {
                    localStorage.clear('a5srf4wer7w1cd');
                    navigate('/login');
                } else {
                    toast.error(data?.error);
                }
            }

            if (data?.msg) {
                toast.success(data?.msg);
                FetchClaimCoupons();
            }
            if (data?.balance) {
                TriggerBalanceUpdate({ balance: data?.balance, usdt: data?.usdt });
            }

        } catch (error) {
            toast.error('Technical error');
            console.log(error);
        } finally {
            setCouponCode('');
            setIsLoading(false);
        }
    };

    useEffect(() => {
        FetchClaimCoupons();
    }, []);

    // THEME-AWARE STYLES
    const pageStyles = {
        minHeight: '100vh',
        background: isDarkMode ? '#020617' : '#ffffff', // slate-950 vs white
        padding: '1rem 0',
        fontSize: '14px',
        color: isDarkMode ? '#e5e7eb' : '#2c3e50',
        transition: 'background 0.25s ease, color 0.25s ease'
    };

    const headerIconStyles = {
        marginBottom: '0.5rem'
    };

    const giftIconStyles = {
        fontSize: '2rem',
        display: 'inline-block'
    };

    const headerTitleStyles = {
        fontSize: '1.5rem',
        fontWeight: 'bold',
        color: isDarkMode ? '#e5e7eb' : '#2c3e50',
        margin: '0 0 0.25rem 0'
    };

    const headerSubtitleStyles = {
        fontSize: '0.9rem',
        color: isDarkMode ? '#9ca3af' : '#6c757d',
        margin: 0
    };

    const formCardStyles = {
        background: isDarkMode ? '#020617' : 'white',
        borderRadius: '0.75rem',
        border: `1px solid ${isDarkMode ? '#1e293b' : '#e9ecef'}`,
        boxShadow: isDarkMode
            ? '0 10px 25px rgba(15, 23, 42, 0.6)'
            : '0 4px 12px rgba(0, 0, 0, 0.08)',
        transition: 'background 0.25s ease, border 0.25s ease, box-shadow 0.25s ease'
    };

    const inputLabelStyles = {
        fontSize: '0.85rem',
        fontWeight: '600',
        color: isDarkMode ? '#e5e7eb' : '#2c3e50',
        marginBottom: '0.5rem',
        display: 'block'
    };

    const inputStyles = {
        border: `1px solid ${isDarkMode ? '#334155' : '#dee2e6'}`,
        borderRadius: '0.5rem',
        fontSize: '0.85rem',
        padding: '0.375rem 0.75rem',
        background: isDarkMode ? '#020617' : '#ffffff',
        color: isDarkMode ? '#e5e7eb' : '#212529'
    };

    const claimButtonStyles = {
        background: isDarkMode
            ? 'linear-gradient(135deg, #2563eb 0%, #7c3aed 100%)'
            : 'linear-gradient(135deg, #0d6efd 0%, #6f42c1 100%)',
        border: 'none',
        borderRadius: '0.5rem',
        whiteSpace: 'nowrap',
        fontSize: '0.8rem',
        padding: '0.375rem 0.75rem',
        fontWeight: '500',
        minWidth: '60px',
        color: '#ffffff'
    };

    const sectionTitleStyles = {
        fontSize: '1.1rem',
        fontWeight: 'bold',
        color: isDarkMode ? '#e5e7eb' : '#2c3e50',
        marginBottom: '0.75rem'
    };

    const emptyStateStyles = {
        background: isDarkMode ? '#020617' : 'white',
        borderRadius: '0.75rem',
        border: `1px solid ${isDarkMode ? '#1f2937' : '#e9ecef'}`,
        boxShadow: isDarkMode
            ? '0 10px 25px rgba(15, 23, 42, 0.6)'
            : '0 4px 12px rgba(0, 0, 0, 0.08)',
        transition: 'background 0.25s ease, border 0.25s ease, box-shadow 0.25s ease'
    };

    const emptyIconStyles = {
        fontSize: '2rem',
        color: isDarkMode ? '#4b5563' : '#adb5bd',
        marginBottom: '0.5rem'
    };

    const emptyTextStyles = {
        fontSize: '0.9rem',
        color: isDarkMode ? '#9ca3af' : '#6c757d',
        fontWeight: '500',
        margin: '0 0 0.25rem 0'
    };

    const emptySubtextStyles = {
        fontSize: '0.8rem',
        color: isDarkMode ? '#6b7280' : '#adb5bd',
        margin: 0
    };

    const couponCardStyles = {
        background: isDarkMode ? '#020617' : 'white',
        borderRadius: '0.75rem',
        border: `1px solid ${isDarkMode ? '#1e293b' : '#e9ecef'}`,
        boxShadow: isDarkMode
            ? '0 10px 25px rgba(15, 23, 42, 0.7)'
            : '0 4px 12px rgba(0, 0, 0, 0.1)',
        transition: 'all 0.25s ease',
        overflow: 'hidden'
    };

    const discountAmountStyles = {
        fontSize: '1rem',
        fontWeight: 'bold',
        color: '#22c55e',
        background: isDarkMode ? 'rgba(22, 163, 74, 0.1)' : '#e8f5e8',
        padding: '0.25rem 0.5rem',
        borderRadius: '0.375rem',
        minWidth: '50px',
        textAlign: 'center',
        border: `1px solid ${isDarkMode ? 'rgba(34, 197, 94, 0.35)' : '#c8e6c9'}`
    };

    const couponCodeStyles = {
        fontSize: '0.9rem',
        fontWeight: '600',
        color: isDarkMode ? '#e5e7eb' : '#2c3e50',
        margin: '0 0 0.125rem 0'
    };

    const claimDateStyles = {
        fontSize: '0.75rem',
        color: isDarkMode ? '#9ca3af' : '#6c757d',
        margin: 0
    };

    const claimedBadgeStyles = {
        fontSize: '0.7rem',
        fontWeight: '600',
        color: '#22c55e',
        background: isDarkMode ? 'rgba(22, 163, 74, 0.1)' : '#e8f5e8',
        padding: '0.25rem 0.5rem',
        borderRadius: '0.375rem',
        border: `1px solid ${isDarkMode ? 'rgba(34, 197, 94, 0.35)' : '#c8e6c9'}`
    };

    const fullWidthCodeContainer = {
        width: '100%',
        background: isDarkMode ? '#020617' : '#f8f9fa',
        padding: '0.5rem',
        borderRadius: '0.375rem',
        border: `1px solid ${isDarkMode ? '#1e293b' : '#e9ecef'}`,
        marginTop: '0.5rem'
    };

    const fullWidthCodeValue = {
        fontSize: '0.85rem',
        color: isDarkMode ? '#e5e7eb' : '#2c3e50',
        fontWeight: '600',
        fontFamily: 'monospace',
        wordBreak: 'break-all'
    };

    return (
        <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="coupon-page-container"
            style={pageStyles}
        >
            <div className="container-fluid px-3">
                {/* Header */}
                <motion.div
                    initial={{ opacity: 0, y: -20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.2 }}
                    className="text-center mb-4"
                >
                    <div style={headerIconStyles}>
                        <span style={giftIconStyles}>🎁</span>
                    </div>
                    <h1 style={headerTitleStyles}>Claim Your Coupons</h1>
                    <p style={headerSubtitleStyles}>
                        Enter coupon codes below to claim amazing discounts!
                    </p>
                </motion.div>

                {/* Claim Form */}
                <motion.div
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ delay: 0.3 }}
                    className="card shadow border-0 mb-4 form-card"
                    style={formCardStyles}
                >
                    <div className="card-body p-3">
                        <form onSubmit={handleClaimCoupon}>
                            <div className="mb-3">
                                <label htmlFor="couponCode" style={inputLabelStyles}>
                                    Enter Coupon Code
                                </label>
                                <div className="d-flex gap-2">
                                    <input
                                        type="text"
                                        id="couponCode"
                                        value={couponCode}
                                        onChange={(e) =>
                                            setCouponCode(e.target.value.toUpperCase())
                                        }
                                        placeholder="e.g., WELCOME10"
                                        className="form-control"
                                        style={inputStyles}
                                        disabled={isLoading}
                                    />
                                    <motion.button
                                        type="submit"
                                        whileHover={{ scale: 1.03 }}
                                        whileTap={{ scale: 0.97 }}
                                        disabled={isLoading || couponCode == ''}
                                        className="btn btn-primary px-3 claim-btn"
                                        style={claimButtonStyles}
                                    >
                                        {isLoading ? (
                                            <motion.div
                                                animate={{ rotate: 360 }}
                                                transition={{
                                                    duration: 1,
                                                    repeat: Infinity,
                                                    ease: 'linear'
                                                }}
                                                className="spinner-border spinner-border-sm"
                                                role="status"
                                                style={{ width: '12px', height: '12px' }}
                                            />
                                        ) : (
                                            'Claim'
                                        )}
                                    </motion.button>
                                </div>
                            </div>
                        </form>
                    </div>
                </motion.div>

                {/* Claimed Coupons List */}
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.4 }}
                >
                    <h2 style={sectionTitleStyles}>Your Claimed Coupons</h2>

                    <AnimatePresence>
                        {claimedCoupons.length === 0 ? (
                            <motion.div
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                exit={{ opacity: 0 }}
                                className="card shadow border-0 text-center py-4 empty-state-card"
                                style={emptyStateStyles}
                            >
                                <div className="card-body p-3">
                                    <div style={emptyIconStyles}>🎁</div>
                                    <p style={emptyTextStyles}>No coupons claimed yet</p>
                                    <p style={emptySubtextStyles}>
                                        Claim your first coupon using the form above!
                                    </p>
                                </div>
                            </motion.div>
                        ) : (
                            <div className="row">
                                <div className="col-12">
                                    {claimedCoupons.map((coupon, index) => (
                                        <motion.div
                                            key={coupon.id}
                                            initial={{ opacity: 0, y: 10 }}
                                            animate={{ opacity: 1, y: 0 }}
                                            exit={{ opacity: 0, y: -10 }}
                                            transition={{ delay: index * 0.05 }}
                                            className="card border-0 mb-3 coupon-card"
                                            style={couponCardStyles}
                                        >
                                            <div className="card-body p-3">
                                                <div className="d-flex justify-content-between align-items-center mb-2">
                                                    {/* Left side - Coupon details */}
                                                    <div
                                                        className="d-flex align-items-center gap-3"
                                                        style={{ flex: 1 }}
                                                    >
                                                        {/* Amount */}
                                                        <div style={discountAmountStyles}>
                                                            +{coupon.amount}
                                                        </div>

                                                        {/* Code and date */}
                                                        <div style={{ flex: 1 }}>
                                                            <div style={couponCodeStyles}>
                                                                {coupon.code}
                                                            </div>
                                                            <div style={claimDateStyles}>
                                                                Claimed on{' '}
                                                                {moment(
                                                                    coupon.claim_date
                                                                ).format('DD/MM/YY')}
                                                            </div>
                                                        </div>
                                                    </div>

                                                    {/* Badge */}
                                                    <div style={claimedBadgeStyles}>Claimed</div>
                                                </div>

                                                {/* Full width coupon code */}
                                                <div style={fullWidthCodeContainer}>
                                                    <div style={fullWidthCodeValue}>
                                                        Coupon: {coupon.coupon_code}
                                                    </div>
                                                </div>
                                            </div>
                                        </motion.div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </AnimatePresence>
                </motion.div>
            </div>
        </motion.div>
    );
};

export default CouponScreen;
